import type { NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import Layout from '@/components/Layout';
import MetricCard from '@/components/MetricCard';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { useEffect, useState, useMemo } from 'react';
import { getDocumentos, getIncidencias, getDatosFinancieros, getClienteIdByUserId } from '@/lib/supabaseService';
import type { Documento, Incidencia, DatosFinancieros } from '@/types';

const ClienteDashboard: NextPage = () => {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [loadingData, setLoadingData] = useState(true);
  const [clienteId, setClienteId] = useState<string | null>(null);

  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [incidencias, setIncidencias] = useState<Incidencia[]>([]);
  const [datosFinancieros, setDatosFinancieros] = useState<DatosFinancieros | null>(null);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'cliente')) {
      router.push('/');
    }
  }, [user, loading, router]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      setLoadingData(true);
      try {
        const cId = await getClienteIdByUserId(user.id);
        if (!cId) { setLoadingData(false); return; }
        setClienteId(cId);

        const [docsData, incData, finData] = await Promise.all([
          getDocumentos(cId),
          getIncidencias(cId),
          getDatosFinancieros(cId),
        ]);
        setDocumentos(docsData);
        setIncidencias(incData);
        setDatosFinancieros(finData);
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setLoadingData(false);
      }
    };
    if (user?.id) fetchData();
  }, [user?.id]);

  const estadisticas = useMemo(() => ({
    pendientes: documentos.filter(d => d.estado === 'pendiente' || d.estado === 'procesado_ia').length,
    contabilizados: documentos.filter(d => d.estado === 'contabilizado').length,
    incidenciasActivas: incidencias.filter(i => i.estado === 'nueva' || i.estado === 'en_revision').length,
    resultado: datosFinancieros?.resultado || 0,
  }), [documentos, incidencias, datosFinancieros]);

  const actividadReciente = useMemo(() => {
    const actividades: Array<{ id: string; tipo: string; titulo: string; fecha: Date; estado?: string }> = [];

    [...documentos]
      .sort((a, b) => new Date(b.fechaSubida).getTime() - new Date(a.fechaSubida).getTime())
      .slice(0, 3)
      .forEach(doc => {
        actividades.push({ id: `doc-${doc.id}`, tipo: 'documento', titulo: `Subiste ${doc.nombre}`, fecha: doc.fechaSubida, estado: doc.estado });
      });

    [...incidencias]
      .sort((a, b) => new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime())
      .slice(0, 2)
      .forEach(inc => {
        actividades.push({
          id: `inc-${inc.id}`, tipo: 'incidencia',
          titulo: inc.origen === 'ia' ? 'La IA marcó una incidencia' : 'Nueva incidencia de tu asesor',
          fecha: inc.fechaCreacion, estado: inc.estado,
        });
      });

    return actividades.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }, [documentos, incidencias]);

  const activityIcon = (tipo: string) => {
    return tipo === 'documento'
      ? <span className="material-symbols-outlined text-sm">description</span>
      : <span className="material-symbols-outlined text-sm">warning</span>;
  };

  const estadoBadge = (estado?: string) => {
    if (!estado) return null;
    const map: Record<string, { cls: string; label: string }> = {
      pendiente: { cls: 'badge-warning', label: 'Pendiente' },
      procesado_ia: { cls: 'badge-info', label: 'En Proceso' },
      validado: { cls: 'badge-success', label: 'Validado' },
      contabilizado: { cls: 'badge-success', label: 'Contabilizado' },
      nueva: { cls: 'badge-warning', label: 'Nueva' },
      en_revision: { cls: 'badge-info', label: 'En Revisión' },
      resuelta: { cls: 'badge-success', label: 'Resuelta' },
    };
    const m = map[estado] || { cls: 'badge-secondary', label: estado };
    return <span className={`badge ${m.cls}`}>{m.label}</span>;
  };

  if (loading || !user || user.rol !== 'cliente' || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-calm-bg">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-calm-accent border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-calm-muted">Cargando tus datos…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Mi Dashboard — Externaliza2</title>
        <link rel="icon" href="/logo.png" />
      </Head>
      <Layout rol="cliente">
        <div className="h-screen overflow-y-auto scrollbar-thin">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-8">

            {/* Header */}
            <div className="animate-fade-in">
              <h1 className="text-5xl font-light font-display text-calm-text">Hola, {user.nombre?.split(' ')[0]}</h1>
              <p className="text-calm-muted mt-1">Resumen de tu empresa</p>
            </div>

            {/* Metric cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="animate-slide-up stagger-1">
                <MetricCard
                  titulo="PENDIENTES"
                  valor={estadisticas.pendientes}
                  descripcion="Sin contabilizar"
                  icono={<span className="material-symbols-outlined">description</span>}
                  accent="bg-warning"
                  href="/cliente/documentos"
                />
              </div>
              <div className="animate-slide-up stagger-2">
                <MetricCard
                  titulo="CONTABILIZADOS"
                  valor={estadisticas.contabilizados}
                  descripcion="Este periodo"
                  icono={<span className="material-symbols-outlined">check_box</span>}
                  accent="bg-success"
                  href="/cliente/documentos"
                />
              </div>
              <div className="animate-slide-up stagger-3">
                <MetricCard
                  titulo="INCIDENCIAS"
                  valor={estadisticas.incidenciasActivas}
                  descripcion="Requieren tu atención"
                  icono={<span className="material-symbols-outlined">warning</span>}
                  accent="bg-error"
                  href="/cliente/incidencias"
                />
              </div>
              <div className="animate-slide-up stagger-4">
                <MetricCard
                  titulo="RESULTADO"
                  valor={datosFinancieros ? `${estadisticas.resultado >= 0 ? '+' : ''}${(estadisticas.resultado / 1000).toFixed(1)}k€` : 'N/D'}
                  descripcion={datosFinancieros ? (estadisticas.resultado >= 0 ? 'Beneficio' : 'Pérdida') : 'Pendiente'}
                  icono={<span className="material-symbols-outlined">euro</span>}
                  accent="bg-cliente"
                  href="/cliente/panel-financiero"
                />
              </div>
            </div>

            {/* Activity + Quick actions row */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 animate-slide-up stagger-5">

              {/* Actividad Reciente */}
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 xl:col-span-2">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-[11px] uppercase tracking-widest text-calm-muted flex items-center gap-2">
                    <span className="material-symbols-outlined text-steel-azure-400 text-base">schedule</span>
                    Actividad Reciente
                  </h2>
                </div>
                {actividadReciente.length > 0 ? (
                  <div className="space-y-1">
                    {actividadReciente.map((act) => (
                      <div key={act.id} className="flex items-center gap-3 px-3 py-2.5 rounded-huge hover:bg-calm-bg transition-colors group">
                        <div className="w-7 h-7 rounded-huge bg-calm-bg flex items-center justify-center text-calm-muted flex-shrink-0">
                          {activityIcon(act.tipo)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-light text-calm-text truncate">{act.titulo}</p>
                          <p className="text-xs text-calm-muted mt-0.5">
                            {new Date(act.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </p>
                        </div>
                        {estadoBadge(act.estado)}
                        <span className="material-symbols-outlined text-calm-muted text-sm opacity-0 group-hover:opacity-100 transition-opacity">chevron_right</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <span className="material-symbols-outlined text-calm-border text-3xl mx-auto mb-2">schedule</span>
                    <p className="text-sm text-calm-muted">Sin actividad reciente</p>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="space-y-3">
                <Link href="/cliente/documentos" className="bg-gradient-to-br from-fresh-sky-50/50 to-white border border-fresh-sky-200/40 rounded-huge p-5 hover:shadow-md hover:-translate-y-0.5 transition-all group flex items-center gap-4">
                  <div className="w-11 h-11 rounded-huge bg-fresh-sky-100 flex items-center justify-center text-fresh-sky-600 flex-shrink-0">
                    <span className="material-symbols-outlined">upload</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-light text-calm-text">Subir Documento</p>
                    <p className="text-xs text-calm-muted mt-0.5">Facturas y recibos</p>
                  </div>
                  <span className="material-symbols-outlined text-calm-muted group-hover:text-fresh-sky-500 transition-colors text-base">chevron_right</span>
                </Link>

                <Link href="/cliente/incidencias" className="bg-gradient-to-br from-steel-azure-50/50 to-white border border-steel-azure-200/40 rounded-huge p-5 hover:shadow-md hover:-translate-y-0.5 transition-all group flex items-center gap-4">
                  <div className="w-11 h-11 rounded-huge bg-steel-azure-100 flex items-center justify-center text-steel-azure-600 flex-shrink-0">
                    <span className="material-symbols-outlined">chat</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-light text-calm-text">Hablar con Asesor</p>
                    <p className="text-xs text-calm-muted mt-0.5">Resolver dudas</p>
                  </div>
                  <span className="material-symbols-outlined text-calm-muted group-hover:text-steel-azure-500 transition-colors text-base">chevron_right</span>
                </Link>

                <Link href="/cliente/panel-financiero" className="bg-gradient-to-br from-emerald-50/50 to-white border border-emerald-200/40 rounded-huge p-5 hover:shadow-md hover:-translate-y-0.5 transition-all group flex items-center gap-4">
                  <div className="w-11 h-11 rounded-huge bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                    <span className="material-symbols-outlined">euro</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-light text-calm-text">Panel Financiero</p>
                    <p className="text-xs text-calm-muted mt-0.5">Métricas detalladas</p>
                  </div>
                  <span className="material-symbols-outlined text-calm-muted group-hover:text-emerald-500 transition-colors text-base">chevron_right</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </Layout>
    </>
  );
};

export default ClienteDashboard;
